import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogClose,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  AlertCircle,
  Copy,
  ExternalLink,
  Facebook,
  Linkedin,
  Loader2,
  MessageCircle,
  Share2,
  Twitter,
  X,
  Check,
} from "lucide-react";
import { useShareChat } from "../hooks/use-share-chat";
import { QRCodeSection } from "./QRCodeSection";
import { SOCIAL_MEDIA_PLATFORMS } from "../types";
import { cn } from "@/lib/utils";

/**
 * ShareLinkDialog Component
 *
 * Zero Props Pattern: Tüm state hook'tan gelir
 * Conditional Rendering: isPublic değerine göre disable butonu gösterilir
 */
export const ShareLinkDialog: React.FC = () => {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);

  // Zero Props: Hook'tan tüm state ve action'lar gelir
  const {
    isOpen,
    shareLink,
    isPublic,
    isPending,
    isDisablePending,
    handleCopyLink,
    handleDisableSharing,
    closeDialog,
  } = useShareChat();

  // Social Media Icon Mapping
  const socialIcons: Record<string, React.ReactNode> = useMemo(
    () => ({
      twitter: <Twitter className="w-5 h-5" />,
      facebook: <Facebook className="w-5 h-5" />,
      whatsapp: <MessageCircle className="w-5 h-5" />,
      linkedin: <Linkedin className="w-5 h-5" />,
    }),
    []
  );

  const handleSocialShare = (platform: string) => {
    if (!shareLink) return;
    const url = SOCIAL_MEDIA_PLATFORMS[platform].url(shareLink);
    window.open(url, "_blank", "width=600,height=400");
  };

  const handleCopy = async () => {
    if (!shareLink) return;
    await handleCopyLink();
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1500);
  };

  return (
    <Dialog open={isOpen} onOpenChange={closeDialog}>
      <DialogContent
        open={isOpen}
        className={cn(
          "sm:max-w-2xl p-0 overflow-hidden rounded-3xl",
          "bg-white dark:bg-gray-900",
          "border border-gray-200/80 dark:border-gray-700/80",
          "shadow-2xl shadow-black/10 dark:shadow-black/40",
          "ring-1 ring-black/5 dark:ring-white/10"
        )}
        data-state={isOpen ? "open" : "closed"}
      >
        {/* Decorative background */}
        <div className="pointer-events-none absolute inset-0 bg-gradient-to-br from-indigo-500/10 via-transparent to-purple-500/10 dark:from-indigo-500/15 dark:to-purple-500/15" />

        <div className="relative z-10 p-6 sm:p-7">
          <DialogHeader className="space-y-2">
            <div className="flex items-start justify-between gap-4">
              <div className="flex items-start gap-3">
                <div className="mt-0.5 flex h-10 w-10 items-center justify-center rounded-2xl bg-indigo-600/10 dark:bg-indigo-500/15">
                  <Share2 className="h-5 w-5 text-indigo-600 dark:text-indigo-300" />
                </div>
                <div className="min-w-0">
                  <DialogTitle className="text-xl font-semibold tracking-tight text-text-primary dark:text-gray-100">
                    {t("chat.share.title")}
                  </DialogTitle>
                  <DialogDescription className="mt-1 text-sm text-text-secondary dark:text-gray-400">
                    {t("chat.share.description")}
                  </DialogDescription>
                </div>
              </div>

              <DialogClose asChild>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-9 w-9 rounded-xl text-text-secondary hover:text-text-primary hover:bg-bg-secondary dark:text-gray-400 dark:hover:text-gray-100 dark:hover:bg-gray-800"
                >
                  <X className="h-4 w-4" />
                  <span className="sr-only">{t("common.close")}</span>
                </Button>
              </DialogClose>
            </div>
          </DialogHeader>

          {/* Loading State */}
          {(isPending || !shareLink) && (
            <div className="mt-6 flex flex-col items-center justify-center rounded-2xl border border-border-secondary dark:border-gray-700 bg-bg-secondary dark:bg-gray-800 py-10 gap-3">
              <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-white/70 dark:bg-gray-900/40 ring-1 ring-black/5 dark:ring-white/10">
                <Loader2 className="h-6 w-6 animate-spin text-indigo-600 dark:text-indigo-300" />
              </div>
              <p className="text-sm font-medium text-text-secondary dark:text-gray-400">
                {t("chat.share.creating")}
              </p>
            </div>
          )}

          {/* Share Link Display */}
          {!isPending && shareLink && (
            <div className="mt-6 space-y-6">
              {/* Link Input */}
              <div className="space-y-2">
                <div className="flex items-center justify-between gap-3">
                  <label className="text-sm font-medium text-text-primary dark:text-gray-100">
                    {t("chat.share.copyLink")}
                  </label>
                  <a
                    href={shareLink}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1 text-xs font-medium text-indigo-600 hover:text-indigo-700 dark:text-indigo-300 dark:hover:text-indigo-200"
                  >
                    <ExternalLink className="h-3.5 w-3.5" />
                    <span className="whitespace-nowrap">
                      {t("common.open")}
                    </span>
                  </a>
                </div>

                <div className="flex flex-col sm:flex-row gap-2">
                  <Input
                    value={shareLink}
                    readOnly
                    onClick={(e) => e.currentTarget.select()}
                    className={cn(
                      "flex-1 font-mono text-xs sm:text-sm",
                      "bg-bg-secondary dark:bg-gray-800",
                      "border-border-secondary dark:border-gray-700",
                      "text-text-primary dark:text-gray-100",
                      "rounded-xl"
                    )}
                  />
                  <Button
                    onClick={handleCopy}
                    className={cn(
                      "rounded-xl h-10 sm:h-auto",
                      copied
                        ? "bg-green-600 hover:bg-green-700 text-white"
                        : "bg-indigo-600 hover:bg-indigo-700 text-white"
                    )}
                  >
                    {copied ? (
                      <>
                        <Check className="h-4 w-4" />
                        {t("chat.share.linkCopied")}
                      </>
                    ) : (
                      <>
                        <Copy className="h-4 w-4" />
                        {t("common.copy")}
                      </>
                    )}
                  </Button>
                </div>
              </div>

              {/* Social Media Share Buttons */}
              <div className="space-y-2">
                <label className="text-sm font-medium text-text-primary dark:text-gray-100">
                  {t("common.share")}
                </label>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                  {Object.keys(SOCIAL_MEDIA_PLATFORMS).map((platform) => (
                    <Button
                      key={platform}
                      onClick={() => handleSocialShare(platform)}
                      variant="outline"
                      className={cn(
                        "h-11 rounded-xl",
                        "flex items-center justify-center gap-2",
                        "border-border-secondary dark:border-gray-700",
                        "bg-white/60 dark:bg-gray-900/30",
                        "hover:bg-bg-secondary dark:hover:bg-gray-800/50",
                        "transition-colors"
                      )}
                    >
                      <span
                        style={{
                          color: SOCIAL_MEDIA_PLATFORMS[platform].color,
                        }}
                      >
                        {socialIcons[platform]}
                      </span>
                      <span className="text-sm font-medium text-text-primary dark:text-gray-100">
                        {SOCIAL_MEDIA_PLATFORMS[platform].name}
                      </span>
                    </Button>
                  ))}
                </div>
              </div>

              {/* QR Code Section */}
              <QRCodeSection link={shareLink} />

              {/* Disable Sharing Button (Only if isPublic) */}
              {isPublic && (
                <div className="rounded-2xl border border-red-200/60 dark:border-red-900/40 bg-red-50/40 dark:bg-red-950/10 p-4">
                  <Button
                    onClick={handleDisableSharing}
                    disabled={isDisablePending}
                    variant="outline"
                    className={cn(
                      "w-full rounded-xl flex items-center justify-center gap-2",
                      "border-red-300 text-red-700 hover:bg-red-50",
                      "dark:border-red-900/60 dark:text-red-300 dark:hover:bg-red-950/20",
                      "disabled:opacity-60 disabled:cursor-not-allowed",
                      "dark:disabled:bg-red-950/10 dark:disabled:text-red-300/70"
                    )}
                  >
                    {isDisablePending ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        {t("chat.share.disabling")}
                      </>
                    ) : (
                      <>
                        <AlertCircle className="w-4 h-4" />
                        {t("chat.share.disableSharing")}
                      </>
                    )}
                  </Button>
                </div>
              )}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
